/**
 * Settings > 패스키 탭 콘텐츠 (#1112 / 685-559 디자인 — Security 인라인 섹션에서 전용 탭으로 분리).
 *
 * 토글의 의미 = "등록된 credential 1개 이상 존재 여부" (별도 backend flag 없음).
 * - Off + 클릭 → PasskeyRegisterModal 오픈, 등록 성공 시 자동 On.
 * - On + 클릭 → 전체 Passkey 삭제 확인 모달 (UMK 잠금 해제 상태 가정).
 */
import React, { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Button, Switch } from 'antd'
import { useTranslation } from 'react-i18next'
import { getUMKFromLocal } from 'pUtils'
import { deleteCredential, listCredentials, isUmkFresh } from 'services/passkey'
import {
  hasRegisteredDevice,
  maybeRegisterCurrentDevice,
  registerCurrentDevice,
  revokeCurrentDevice,
} from 'services/passkey/deviceUnlock'
import { confirmDelete, errorBase } from 'utils/ui'

import PasskeyRegisterModal from 'pages/auth/components/PasskeyRegisterModal'
import ManagePasskeysModal from './ManagePasskeysModal'
import { showStaleSessionPopup } from './staleSessionPopup'
import styles from '../style.module.scss'

const PasskeyManagement = () => {
  const { t } = useTranslation()
  const [credentials, setCredentials] = useState([])
  const [registerOpen, setRegisterOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const [umk, setUmk] = useState(null)
  const [busy, setBusy] = useState(false)
  // 토글(서버 credential 존재)과 별개인 "현재 브라우저" 디바이스 키 상태 (IndexedDB).
  // 다른 브라우저에서 패스키를 만든 사용자가 이 브라우저의 자동 잠금해제를 명시 등록할 수 있어야 한다.
  const [deviceRegistered, setDeviceRegistered] = useState(false)
  const [deviceBusy, setDeviceBusy] = useState(false)

  const refresh = useCallback(async () => {
    try {
      const list = await listCredentials()
      setCredentials(list)
    } catch {
      setCredentials([])
    }
  }, [])

  const refreshDevice = useCallback(async () => {
    try {
      setDeviceRegistered(await hasRegisteredDevice())
    } catch {
      setDeviceRegistered(false)
    }
  }, [])

  useEffect(() => {
    refresh()
    refreshDevice()
  }, [refresh, refreshDevice])

  const hasCredentials = credentials.length > 0

  const handleEnable = async () => {
    const current = await getUMKFromLocal(true)
    if (!current) {
      // UMK 만료/잠금 — 조용한 무반응 대신 안내 (#1319 ⑧)
      errorBase({ title: t('appConfig.settings.passkeyUmkExpired') })
      return
    }
    // #1509 후속: 다른 브라우저에서 비번 변경 시 캐시 UMK 가 stale → credential 만 등록되는
    // 반쪽 실패를 ceremony 전에 차단. stale 이면 재로그인 안내(handleRegisterDevice 845 와 동일 문구).
    if (!(await isUmkFresh(current))) {
      showStaleSessionPopup(t)
      return
    }
    setUmk(current)
    setRegisterOpen(true)
  }

  const handleDisable = () => {
    // 다른 errorBase 팝업과 동일한 룩앤필(빨간 경고 아이콘 + 스타일된 destructive 버튼)을 위해
    // confirmDelete 사용. 취소/확인 2버튼 확인 시맨틱은 유지하되 okText 만 '확인'으로 둔다.
    confirmDelete({
      icon: 'red',
      title: t('appConfig.settings.passkeyDisableConfirmTitle'),
      content: t('appConfig.settings.passkeyDisableConfirmContent'),
      okText: t('common.button.ok'),
      onOk: async () => {
        setBusy(true)
        try {
          // #1319 ①: state 의 credentials 는 마운트 시점 조회분(stale 가능 — 다른 브라우저에서
          // 추가된 패스키가 삭제 누락돼 "모든 패스키 삭제" 안내 위반). OFF 직전 서버 최신 재조회.
          let targets = credentials
          try {
            targets = await listCredentials()
          } catch (e) {
            console.warn('[passkey] pre-disable refresh failed, falling back to cached list:', e)
          }
          const results = await Promise.allSettled(targets.map((cred) => deleteCredential(cred.id)))
          if (results.some((r) => r.status === 'rejected')) {
            errorBase({ title: t('appConfig.settings.passkeyDisablePartialFail') })
          }
          // DBE Keeper: 패스키 비활성 = 자동 잠금해제도 비활성 → 서버 디바이스 해제 + IndexedDB 정리
          await revokeCurrentDevice()
          setDeviceRegistered(false)
        } finally {
          await refresh()
          setBusy(false)
        }
      },
      okButtonProps: { className: classNames('type8', 'h38', 'w100') },
    })
  }

  const handleSwitchChange = (checked) => {
    if (busy) return
    if (checked) handleEnable()
    else handleDisable()
  }

  // 디바이스 자동 잠금해제 토글 — 패스키 잠금해제 토글과 동일 UX (ON=등록, OFF=해제 confirm)
  const handleDeviceSwitchChange = (checked) => {
    if (deviceBusy) return
    if (checked) handleRegisterDevice()
    else handleRemoveDevice()
  }

  // '이 브라우저에 생성': 기존 패스키 assertion 만으로 현 디바이스 키 등록 (새 패스키 생성 불필요).
  const handleRegisterDevice = async () => {
    if (deviceBusy) return
    // busy 를 getUMKFromLocal(production 에서 AES 복호화 수백 ms) await 전에 올려야
    // 대기 중 재클릭 → WebAuthn 중복 요청(InvalidStateError)을 막는다 (Gemini 리뷰 #1298).
    setDeviceBusy(true)
    try {
      const currentUmk = await getUMKFromLocal(true)
      if (!currentUmk) return
      // stale UMK 면 WebAuthn assertion 프롬프트를 띄우기 전에 미리 차단 (아래 845 catch 와 동일 안내).
      // registerCurrentDevice 는 프롬프트 후에야 old_hmk→845 를 받으므로, 사전 검증이 헛프롬프트를 막는다.
      if (!(await isUmkFresh(currentUmk))) {
        showStaleSessionPopup(t)
        return
      }
      await registerCurrentDevice(currentUmk)
      setDeviceRegistered(true)
    } catch (e) {
      // #1509: 세션 UMK 가 최신이 아님(다른 기기에서 비번 변경 후 이 탭 미갱신) → 재로그인 안내.
      // stale UMK 로 디바이스가 등록되면 이후 자동 잠금해제가 옛 UMK 를 돌려줘 로그인 실패하므로 서버가 845 로 차단.
      if (e?.code === 845) {
        showStaleSessionPopup(t)
      } else if (e?.name !== 'NotAllowedError' && e?.name !== 'TimeoutError') {
        // WebAuthn 사용자 취소/타임아웃은 의도된 중단 — 안내 없이 폴백 (LoginMasterPW 와 동일 정책)
        console.warn('[DBE] registerCurrentDevice failed:', e)
        errorBase({ title: t('appConfig.settings.passkeyDeviceCreateFailed') })
      }
    } finally {
      setDeviceBusy(false)
    }
  }

  // '이 브라우저에서 제거': 현 디바이스만 해제 (credential/패스키는 유지 — 토글·타 브라우저 무영향).
  // 제거는 [생성] 버튼으로 즉시 복구 가능하므로 가벼운 confirm 1회 (dbe_passkey_ux_followup.md §4).
  const handleRemoveDevice = () => {
    if (deviceBusy) return
    confirmDelete({
      icon: 'red',
      title: t('appConfig.settings.passkeyDeviceRemoveConfirmTitle'),
      content: t('appConfig.settings.passkeyDeviceRemoveConfirmContent'),
      okText: t('common.button.ok'),
      onOk: async () => {
        setDeviceBusy(true)
        try {
          await revokeCurrentDevice() // 서버 해제 + IndexedDB 정리. 서버 실패해도 로컬 정리 진행
          setDeviceRegistered(false)
        } catch (e) {
          // idbClearUser 등 로컬 정리 실패는 전파됨 — unhandled rejection 방지.
          // 등록 상태는 유지(실제로 정리 안 됐으므로 true 가 정확) → 사용자가 재시도 가능.
          console.warn('[DBE] revokeCurrentDevice failed:', e)
        } finally {
          setDeviceBusy(false)
        }
      },
      okButtonProps: { className: classNames('type8', 'h38', 'w100') },
    })
  }

  // ManagePasskeysModal 의 refresh useCallback dep 로 들어가므로
  // 인라인 함수면 매 렌더마다 refresh identity 가 바뀌어 useEffect 무한 루프 발생.
  const handleManageChanged = useCallback((list) => setCredentials(list), [])
  // PR-2 (#1301): 관리 모달 안에서 '새 패스키 등록' 시 디바이스 자동 등록이 일어날 수 있으므로
  // 모달 닫힘 시 디바이스 상태 행을 재조회 (콜백 추가 없이 단순 — 설계 §5.2-(3))
  const handleManageClose = useCallback(() => {
    setManageOpen(false)
    refreshDevice()
  }, [refreshDevice])

  // 관리 모달에서 마지막 패스키 삭제 = 토글 OFF 와 같은 상태 → handleDisable 과 동일하게
  // 디바이스 해제(서버 + IndexedDB). 누락 시 잠금 화면(LoginMasterPW)이 IndexedDB 의
  // device-id 를 보고 '패스키로 잠금해제' 버튼을 계속 노출한다.
  const handleLastDeviceDeleted = useCallback(async () => {
    await revokeCurrentDevice()
    await refresh()
  }, [refresh])

  return (
    <div className={styles.passkeySection}>
      <div className={styles.passkeyRow}>
        <span className={styles.passkeyLabel}>{t('appConfig.settings.passkeyUnlock')}</span>
        <Switch
          checked={hasCredentials}
          onChange={handleSwitchChange}
          disabled={busy}
          className={classNames(styles.passkeySwitch)}
        />
      </div>

      {hasCredentials && (
        <div className={classNames(styles.passkeyRow, styles.passkeyManageRow)}>
          <span className={styles.passkeyCount}>
            {t('appConfig.settings.passkeyCount', { count: credentials.length })}
          </span>
          <div style={{ flex: '1 0 0' }} />
          <Button className={styles.manageBtn} onClick={() => setManageOpen(true)}>
            {t('appConfig.settings.passkeyManageBtn')}
          </Button>
        </div>
      )}

      {hasCredentials && (
        <div className={styles.passkeyRow}>
          <span className={styles.passkeyCount}>
            {t('appConfig.settings.passkeyDeviceNotRegistered')}
          </span>
          <div style={{ flex: '1 0 0' }} />
          <Switch
            checked={deviceRegistered}
            onChange={handleDeviceSwitchChange}
            disabled={deviceBusy}
            className={classNames(styles.passkeySwitch)}
          />
        </div>
      )}

      <PasskeyRegisterModal
        open={registerOpen}
        umk={umk}
        onClose={() => {
          setRegisterOpen(false)
          setUmk(null)
        }}
        onRegistered={async (result) => {
          const currentUmk = umk
          setRegisterOpen(false)
          setUmk(null)
          await refresh()
          // DBE Keeper §4.2 C1 + PR-2 (#1301): 첫 패스키 등록 직후 이 디바이스 자동 키 등록.
          // result.deviceRegistrationToken 으로 두 번째 WebAuthn 프롬프트 생략 (실패 비차단).
          // #1319 ③: 실패가 조용히 사라지면 사용자가 자동 잠금해제 미설정 상태를 모름 → 안내
          const deviceReady = await maybeRegisterCurrentDevice(currentUmk, result)
          if (!deviceReady) {
            errorBase({ title: t('appConfig.settings.passkeyDeviceCreateFailed') })
          }
          await refreshDevice()
        }}
      />
      <ManagePasskeysModal
        open={manageOpen}
        onClose={handleManageClose}
        onChanged={handleManageChanged}
        onLastDeviceDeleted={handleLastDeviceDeleted}
      />
    </div>
  )
}

export default PasskeyManagement
